import { CloseOutlined } from '@ant-design/icons';
import clsx from 'clsx';
import { upperFirst } from 'es-toolkit';

import { IconButton } from '@/components/atoms/IconButton';
import { Label } from '@/components/atoms/Typography/Label';

import styles from './Alert.module.css';
import { getIconElement } from './utils';

import type { AlertProps } from './types';

/**
 * Displays contextual feedback for status, validation, and inline system messages.
 *
 * Use `title` for a two-line alert with a heading, or omit it for a compact inline
 * alert. `message` is the plain-text body shortcut; pass `children` when the body
 * needs richer React content. Icons are opt-in via `showIcon`. Use `borderless`
 * and `borderRadiusSize` for surface-level styling without changing alert content.
 */
export function Alert({
  alignIcon = 'center',
  borderless,
  borderRadiusSize = 'md',
  children,
  className,
  icon,
  message,
  onClose,
  paddingSize = 'sm',
  showIcon,
  title,
  type = 'info',
}: AlertProps) {
  const body = message ?? children;
  const formattedBody = typeof body === 'string' ? upperFirst(body) : body;
  const hasBody = body !== undefined && body !== null && body !== false && body !== '';
  const iconElement = showIcon ? getIconElement(type, icon) : null;
  const isTitled = Boolean(title);
  const labelVariant = type === 'default' ? 'secondary' : type;
  const alertStyle = {
    '--border-radius-size': `var(--border-radius-${borderRadiusSize})`,
  } as React.CSSProperties;

  return (
    <div
      className={clsx(
        styles.container,
        styles[type],
        styles[alignIcon],
        styles[paddingSize],
        borderless && styles.borderless,
        className
      )}
      role="alert"
      style={alertStyle}
    >
      <div
        className={clsx(
          styles.content,
          isTitled ? styles.stackedContent : styles.inlineContent,
          !isTitled && styles[alignIcon]
        )}
      >
        {isTitled && title ? (
          <>
            <div className={styles.header}>
              {iconElement}
              <Label variant={labelVariant} weight="medium">
                {upperFirst(title)}
              </Label>
            </div>
            {hasBody && <span className={styles.messageContainer}>{formattedBody}</span>}
          </>
        ) : (
          <>
            {iconElement}
            {hasBody && <span className={styles.messageContainer}>{formattedBody}</span>}
          </>
        )}
      </div>

      {onClose && (
        <IconButton label="Close alert" onClick={onClose} className={styles.closeAlert}>
          <CloseOutlined />
        </IconButton>
      )}
    </div>
  );
}

Alert.displayName = 'Alert';
